import { tryOnScopeDispose } from '@vueuse/shared';
import { ref, toValue, type MaybeRefOrGetter } from 'vue';

// Прелоадит первое hero-изображение в blob (для быстрого LCP): остальные
// слайды рендерятся напрямую по своим URL, без дополнительной предзагрузки.
// Созданный blob-URL освобождается при размонтировании.
export const useHeroImagePreloader = (images: MaybeRefOrGetter<string[]>) => {
  const preloadedImages = ref<string[]>([]);
  const objectUrls = new Set<string>();

  const loadImage = async (src: string) => {
    const response = await fetch(src, { cache: 'force-cache' });

    if (!response.ok) {
      throw new Error(`Failed to fetch image ${src}`);
    }

    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    objectUrls.add(objectUrl);

    return objectUrl;
  };

  // Подставляет переданный URL первого слайда и актуальный хвост списка,
  // не затирая уже загруженные слайды устаревшим состоянием. Используется и
  // при завершении предзагрузки первой картинки, и при обновлении списка
  // извне (см. syncImages).
  const withFirstImage = (firstImage: string | undefined, latestImages: string[]) =>
    firstImage ? [firstImage, ...latestImages.slice(1)] : latestImages;

  // Синхронизирует preloadedImages с актуальным списком `images`, сохраняя
  // уже предзагруженный первый слайд (если есть). Вызывать, когда список
  // слайдов обновился извне (например, пришли данные BFF поверх
  // SSR-предзагрузки первого слайда) — не сбрасывает выполненный preload.
  const syncImages = () => {
    preloadedImages.value = withFirstImage(preloadedImages.value[0], toValue(images));
  };

  const preloadImages = async () => {
    const [firstImage] = toValue(images);

    if (!firstImage) {
      return;
    }

    const firstImageBlobUrl = await loadImage(firstImage);

    // Пока грузилась первая картинка, список слайдов мог успеть пополниться —
    // подставляем предзагруженный URL на первое место, не затирая остальные.
    preloadedImages.value = withFirstImage(firstImageBlobUrl, toValue(images));
  };

  tryOnScopeDispose(() => {
    objectUrls.forEach((url) => {
      URL.revokeObjectURL(url);
    });
    objectUrls.clear();
  });

  return { preloadedImages, preloadImages, syncImages };
};
